import { jwtVerify, importSPKI } from 'jose';
import {
  NEXXUS_RELEASE_AUDIENCE,
  NEXXUS_RELEASE_ISSUER,
  NEXXUS_RELEASE_KEY_ID,
  NEXXUS_RELEASE_PUBLIC_KEY,
} from './ReleasePublicKey';
import type { SignedReleaseManifestClaims } from './NexxusUpdateManifest';

export interface ManifestVerifyResult {
  valid: boolean;
  claims?: SignedReleaseManifestClaims;
  error?: string;
}

export interface ReleaseManifestVerificationConfig {
  publicKeyPem: string;
  keyId: string;
  issuer: string;
  audience: string;
}

const DEFAULT_CONFIG: ReleaseManifestVerificationConfig = {
  publicKeyPem: NEXXUS_RELEASE_PUBLIC_KEY,
  keyId: NEXXUS_RELEASE_KEY_ID,
  issuer: NEXXUS_RELEASE_ISSUER,
  audience: NEXXUS_RELEASE_AUDIENCE,
};

const REQUIRED_CLAIMS: (keyof SignedReleaseManifestClaims)[] = [
  'version',
  'platform',
  'architecture',
  'download_url',
  'checksum_sha256',
  'mandatory',
  'published_at',
];

/** Vérifie la signature ES256 et lie le JWT au manifeste reçu. */
export async function verifyReleaseManifestJwt(
  jwt: string,
  expected: SignedReleaseManifestClaims,
  config: ReleaseManifestVerificationConfig = DEFAULT_CONFIG,
): Promise<ManifestVerifyResult> {
  if (!jwt?.trim()) {
    return { valid: false, error: 'manifest_signature absent' };
  }
  try {
    const key = await importSPKI(config.publicKeyPem, 'ES256');
    const { payload, protectedHeader } = await jwtVerify(jwt, key, {
      algorithms: ['ES256'],
      issuer: config.issuer,
      audience: config.audience,
    });
    if (protectedHeader.kid !== config.keyId) {
      return { valid: false, error: 'clé de signature inconnue' };
    }
    const claims = payload as unknown as SignedReleaseManifestClaims;
    for (const claim of REQUIRED_CLAIMS) {
      if (claims[claim] !== expected[claim]) {
        return { valid: false, error: `claim ${claim} incohérent` };
      }
    }
    for (const claim of ['local_schema_version', 'api_compatibility'] as const) {
      if (expected[claim] !== undefined && claims[claim] !== expected[claim]) {
        return { valid: false, error: `claim ${claim} incohérent` };
      }
    }
    return { valid: true, claims };
  } catch (err) {
    return { valid: false, error: err instanceof Error ? err.message : String(err) };
  }
}
